import { FORM_STEPS } from '@/config/addDeviceFlow';
import { staticDefaults } from '@/config/static/catalog';
import type { DeviceDraft, DeviceSetupSuccess, FormStepId } from '@/types/deviceSetup';
import type { GuidedFlowDefinition } from './types';

const manufacturerOptions = Object.keys(staticDefaults.device.manufacturers).map((item) => ({
  value: item,
  label: item,
}));

const deviceTypeOptions = [
  {
    value: 'door-controller',
    label: 'Door controller',
    description: 'The access control panel this site reaches on the network.',
  },
  {
    value: 'video-recorder',
    label: 'Video recorder',
    description: 'Video hardware or recorder on the network for cameras.',
  },
];

export const deviceSetupFlow: GuidedFlowDefinition<DeviceDraft, FormStepId, DeviceSetupSuccess> = {
  id: 'device-setup',
  formTitle: 'Add controller',
  systemLabelPrefix: 'System',
  steps: FORM_STEPS,
  schemas: {
    'device-type': {
      id: 'device-type',
      layout: 'stack',
      fields: [
        {
          id: 'category',
          type: 'card-picker',
          label: 'Device type',
          heading: 'Door controller or video hardware?',
          subheading:
            'Pick one. Door controller is the access panel on the network. Video recorder is for cameras or recorders.',
          options: deviceTypeOptions,
        },
      ],
      backLabel: 'Cancel',
      nextLabel: 'Next',
    },
    manufacturer: {
      id: 'manufacturer',
      layout: 'dual-column',
      fields: [
        {
          id: 'manufacturer',
          type: 'list-single',
          label: 'Manufacturer',
          options: manufacturerOptions,
          derivePatch: (value) => ({
            manufacturer: value,
            model: staticDefaults.device.manufacturers[value]?.[0] ?? '',
          }),
        },
        {
          id: 'model',
          type: 'list-single',
          label: 'Model',
          listIcon: 'device-controller',
          options: (draft) =>
            draft.manufacturer
              ? (staticDefaults.device.manufacturers[draft.manufacturer] ?? []).map((item) => ({
                  value: item,
                  label: item,
                }))
              : [],
        },
      ],
      backLabel: 'Back',
      nextLabel: 'Next',
    },
    details: {
      id: 'details',
      layout: 'grid',
      instruction: {
        operator: '',
        novice: 'Name the controller, then enter network address and panel login from your install notes.',
      },
      fields: [
        { id: 'name', type: 'text', label: 'Device name', required: true, placeholder: 'Enter device name', aiField: 'name' },
        { id: 'description', type: 'text', label: 'Description', placeholder: 'Enter description' },
        {
          id: 'deviceServer',
          type: 'select',
          label: 'Device server',
          aiField: 'deviceServer',
          options: [{ value: 'localhost', label: 'localhost' }],
        },
        {
          id: 'connectionType',
          type: 'select',
          label: 'Connection type',
          aiField: 'connectionType',
          options: [{ value: 'HTTP', label: 'HTTP' }],
        },
        { id: 'username', type: 'text', label: 'User name', required: true, placeholder: 'Enter user name' },
        { id: 'password', type: 'password', label: 'Password', required: true, placeholder: 'Enter password' },
        { id: 'ipAddress', type: 'text', label: 'IP Address', required: true, placeholder: 'Enter IP Address', aiField: 'ipAddress' },
        { id: 'port', type: 'number', label: 'Port', placeholder: '80', aiField: 'port' },
      ],
      backLabel: 'Back',
      nextLabel: 'Next',
    },
    confirm: {
      id: 'confirm',
      fields: [],
      backLabel: 'Back',
      nextLabel: 'Confirm',
    },
  },
  approvalRows: [
    { key: 'Device name', value: (draft) => draft.name || '—', aiField: 'name' },
    { key: 'Description', value: (draft) => draft.description || '—' },
    { key: 'Manufacturer', value: (draft) => draft.manufacturer || '—', aiField: 'manufacturer' },
    { key: 'Model', value: (draft) => draft.model || '—', aiField: 'model' },
    { key: 'Device server', value: (draft) => draft.deviceServer || '—', aiField: 'deviceServer' },
    { key: 'Connection type', value: (draft) => draft.connectionType || 'HTTP', aiField: 'connectionType' },
  ],
  success: {
    bannerText: (result) => `${result.controllerName} added successfully.`,
    noteText: 'Added to this system. Door changes apply after sync and when the panel is online.',
  },
};
